import React from 'react';
import { Clock, DollarSign, HeadphonesIcon, FileCheck, Scale, Trophy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

export const WhyChooseSection = () => {
  const reasons = [
    {
      icon: Clock,
      title: 'Fast & Efficient',
      description: 'Quick turnaround on application preparation with same-day filing capability. We understand that time is money in business, so we prioritize speed without sacrificing quality. Most applications are prepared and ready for filing within 24-48 hours of receiving your information.'
    },
    {
      icon: DollarSign,
      title: 'Transparent Pricing',
      description: 'No hidden fees or surprise charges. Our clear, upfront pricing means you know exactly what you\'ll pay before we begin. We offer flexible payment plans and package options to fit any budget, from startups to established enterprises. What you see is what you pay—no bait and switch tactics.'
    },
    {
      icon: HeadphonesIcon,
      title: 'Expert Support',
      description: 'Every client receives a dedicated case manager who serves as your single point of contact throughout the entire process. Our team includes experienced trademark professionals who are available to answer questions, provide guidance, and address concerns. You\'ll never feel abandoned or confused about your application status.'
    },
    {
      icon: FileCheck,
      title: 'Comprehensive Service',
      description: 'We provide full-service trademark solutions that go far beyond simply filing paperwork. From initial consultation and comprehensive searches through registration, monitoring, enforcement, and renewals, we handle every aspect of trademark protection. Our holistic approach ensures nothing falls through the cracks.'
    },
    {
      icon: Scale,
      title: 'Legal Expertise',
      description: 'Our team brings over 15 years of specialized experience in trademark law and USPTO procedures. We stay current on the latest examination guidelines, case law developments, and procedural changes to provide you with cutting-edge strategies. Our deep knowledge of trademark law means better results for your applications.'
    },
    {
      icon: Trophy,
      title: 'Proven Track Record',
      description: 'With a 95% success rate and over 1,000 trademarks successfully registered, our results speak for themselves. We\'ve handled applications in virtually every industry and overcome complex office actions that other firms couldn\'t resolve. Our track record gives you confidence that your trademark is in expert hands.'
    }
  ];

  return (
    <section id="why-choose" className="py-20 bg-[#EFEBE9]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#3E2723] mb-4">
            Why Choose TradeMark Registry?
          </h2>
          <p className="text-lg text-[#8D6E63] max-w-3xl mx-auto mb-6">
            We combine legal expertise, operational efficiency, and personalized service to deliver the best trademark registration experience in the industry.
          </p>
          <p className="text-lg text-[#8D6E63] max-w-4xl mx-auto">
            Choosing the right trademark service provider is crucial for protecting your brand. Unlike general practice attorneys or automated filing services, we specialize exclusively in trademark registration, which means deeper expertise, better results, and more cost-effective service. Our focused approach allows us to navigate the USPTO system more efficiently and handle complex situations with confidence.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((reason, index) => {
            const Icon = reason.icon;
            return (
              <Card
                key={index}
                className="border-2 border-[#D7CCC8] hover:border-[#8D6E63] transition-all duration-300 hover:shadow-xl bg-white group"
              >
                <CardHeader>
                  <div className="w-14 h-14 bg-[#EFEBE9] rounded-full flex items-center justify-center mb-4 group-hover:bg-[#3E2723] transition-colors duration-300">
                    <Icon className="w-7 h-7 text-[#3E2723] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <CardTitle className="text-xl font-bold text-[#3E2723]">
                    {reason.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-[#8D6E63] leading-relaxed">
                    {reason.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-12 bg-white border-2 border-[#D7CCC8] rounded-lg p-8">
          <h3 className="text-2xl font-bold text-[#3E2723] mb-4 text-center">
            The TradeMark Registry Difference
          </h3>
          <p className="text-[#8D6E63] leading-relaxed max-w-4xl mx-auto text-center">
            We don't just process trademark applications—we build lasting relationships with our clients. Many of our clients return to us for multiple trademark registrations as their businesses grow, and they refer colleagues and friends because of the exceptional service they've received. Our success is measured not just in registrations obtained, but in the long-term success and protection of the brands we help create. When you choose TradeMark Registry, you're choosing a partner who genuinely cares about your brand's future.
          </p>
        </div>
      </div>
    </section>
  );
};
